import React from "react";
import { Box, Group, SegmentedControl } from "@mantine/core";
import { useCalendar } from "../../../../../../context/CalendarContext";
import {
  CalendarEventType,
  SlotInfo as CalendarSlotInfo,
} from "../../../../../../types/calendar";
import DayView from "../../Views/DayView";
import WeekView from "../../Views/WeekView";
import { CalendarDropInfo } from "../../Views/DnDCalendar";

interface SchedulingCalendarProps {
  /** Merged real + transient events to render on the calendar. */
  events: CalendarEventType[];
  /** Fired when the user clicks an empty slot to schedule the active service. */
  onSelectSlot: (slot: CalendarSlotInfo) => void;
  /** Fired when the user clicks an event (used to re-select a transient one). */
  onSelectEvent?: (event: CalendarEventType) => void;
  /** Fired when a proposed event is dragged to a new slot (reschedule). */
  onEventDrop?: (info: CalendarDropInfo) => void;
}

const noop = () => {};

// Only the proposed (transient) events can be dragged; existing appointments are
// read-only context.
const isTransientEvent = (event: CalendarEventType): boolean =>
  !!(event as { isTransient?: boolean }).isTransient;

// Resizing is disabled here — durations are fixed in this view.
const notResizable = () => false;

/**
 * Left pane of the rebooking scheduling screen. Reuses the app's real Day/Week
 * calendar views (so existing appointments, staff columns, working-hours shading
 * and the salon-now indicator all render exactly as on the main calendar) with a
 * local Day/Week toggle. The toggle and date navigation are driven through
 * CalendarContext; RebookModal snapshots & restores that global state around the
 * scheduling step so the underlying calendar is left untouched.
 */
export default function SchedulingCalendar({
  events,
  onSelectSlot,
  onSelectEvent,
  onEventDrop,
}: SchedulingCalendarProps) {
  const { viewType, setViewType } = useCalendar();
  const view = viewType === "week" ? "week" : "day";

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      <Group justify="flex-end" mb="xs">
        <SegmentedControl
          size="xs"
          value={view}
          onChange={(value) => setViewType(value as "day" | "week")}
          data={[
            { label: "Day", value: "day" },
            { label: "Week", value: "week" },
          ]}
        />
      </Group>

      <Box
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {view === "week" ? (
          <WeekView
            events={events}
            onSelectEvent={onSelectEvent ?? noop}
            onSelectSlot={onSelectSlot}
            onEventDrop={onEventDrop}
            draggableAccessor={isTransientEvent}
            resizableAccessor={notResizable}
            resizable={false}
          />
        ) : (
          <DayView
            events={events}
            onSelectEvent={onSelectEvent ?? noop}
            onSelectSlot={onSelectSlot}
            onEventDrop={onEventDrop}
            draggableAccessor={isTransientEvent}
            resizableAccessor={notResizable}
            resizable={false}
          />
        )}
      </Box>
    </Box>
  );
}
